import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAccessibility } from '../context/AccessibilityContext';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [textSize, setTextSize] = useState('medium');
  const [isPaused, setIsPaused] = useState(false);
  const { reduceMotion, setReduceMotion, highContrast, setHighContrast } = useAccessibility();
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const utteranceRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    closeButtonRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
        return;
      }
      if (e.key === 'Tab' && dropdownRef.current) {
        const focusable = dropdownRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    const sizes = { small: '14px', medium: '16px', large: '18px', 'extra-large': '20px' };
    document.documentElement.style.fontSize = sizes[textSize] || '16px';
  }, [textSize]);

  useEffect(() => {
    // When reduced motion is enabled, force-pause videos.
    // When disabled, do not force-play (avoids play() promise rejection / race with pause()).
    if (!reduceMotion) return;
    document.querySelectorAll('video').forEach((v) => {
      try {
        v.pause();
      } catch {
        // ignore
      }
    });
  }, [reduceMotion]);

  const getPageText = () => {
    const body = document.querySelector('main') || document.body;
    const clone = body.cloneNode(true);
    clone.querySelectorAll('script, style, nav, .accessibility-widget, button, [aria-hidden="true"]')
      .forEach(el => el.remove());
    return (clone.textContent || clone.innerText).replace(/\s+/g, ' ').replace(/\n+/g, '. ').trim();
  };

  const speakText = () => {
    if (!('speechSynthesis' in window)) {
      alert('Sorry, your browser does not support text-to-speech functionality.');
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(getPageText());
    utteranceRef.current = utterance;
    utterance.rate = 1.0;
    utterance.pitch = 0.9;
    utterance.volume = 1;
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      const pick = (fn) => voices.find(fn);
      const selected =
        pick(v => v.lang.includes('en-AU') && /premium|neural|enhanced/i.test(v.name) && /male|william|james/i.test(v.name)) ||
        pick(v => v.lang.includes('en-AU') && /male|william|james/i.test(v.name)) ||
        pick(v => v.lang.includes('en-AU')) ||
        pick(v => v.lang.includes('en') && /premium|neural|enhanced/i.test(v.name) && !/female/i.test(v.name)) ||
        pick(v => v.lang.includes('en') && /male|james|david/i.test(v.name)) ||
        pick(v => v.lang.includes('en')) ||
        voices[0];
      if (selected) utterance.voice = selected;
    };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    utterance.onstart = () => { setIsReading(true); setIsPaused(false); };
    utterance.onend = () => { setIsReading(false); setIsPaused(false); };
    utterance.onerror = () => { setIsReading(false); setIsPaused(false); };
    window.speechSynthesis.speak(utterance);
  };

  const pauseSpeech = () => { if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) { window.speechSynthesis.pause(); setIsPaused(true); } };
  const resumeSpeech = () => { if (window.speechSynthesis.paused) { window.speechSynthesis.resume(); setIsPaused(false); } };
  const stopSpeech = () => { window.speechSynthesis.cancel(); setIsReading(false); setIsPaused(false); };

  const toggleReduceMotion = useCallback(() => {
    setReduceMotion(prev => {
      const next = !prev;
      return next;
    });
  }, [setReduceMotion]);

  const toggleHighContrast = useCallback(() => {
    setHighContrast(prev => {
      const next = !prev;
      return next;
    });
  }, [setHighContrast]);

  const hc = highContrast;

  const panelBg = hc ? '#111' : '#ffffff';
  const panelBorder = hc ? '#ffff00' : '#e5e7eb';
  const headerBg = hc ? '#000' : '#0F3656';
  const headerText = hc ? '#ffff00' : '#ffffff';
  const sectionBorder = hc ? '#333' : '#e5e7eb';
  const labelColor = hc ? '#fff' : '#1f2937';
  const subColor = hc ? '#ccc' : '#6b7280';
  const footerBg = hc ? '#000' : '#f9fafb';
  const footerText = hc ? '#ccc' : '#374151';
  const iconColor = hc ? '#ffff00' : '#0F3656';
  const accentBg = hc ? '#ffff00' : '#0F3656';
  const accentText = hc ? '#000' : '#fff';
  const accentHoverBg = hc ? '#ffcc00' : '#0a2a44';
  const mutedBg = hc ? '#333' : '#d1d5db';
  const switchKnob = hc ? '#000' : '#fff';
  const selectedBorder = hc ? '#ffff00' : '#0F3656';
  const selectedBg = hc ? '#ffff00' : '#0F3656';
  const selectedText = hc ? '#000' : '#fff';
  const unselectedBorder = hc ? '#666' : '#d1d5db';
  const unselectedText = hc ? '#fff' : '#374151';

  const ToggleSwitch = ({ on, onToggle, label }) => (
    <button
      onClick={onToggle}
      style={{ backgroundColor: on ? accentBg : mutedBg }}
      className="relative w-12 h-6 rounded-full transition-all duration-150 hover:opacity-90 active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer select-none"
      role="switch"
      aria-checked={on}
      aria-label={label}
    >
      <span
        style={{ backgroundColor: on ? switchKnob : '#fff' }}
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full shadow transition-transform duration-150 ease-out ${on ? 'translate-x-6' : 'translate-x-0'}`}
      />
    </button>
  );

  return (
    <div className="accessibility-widget fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
      {isOpen && (
        <div
          ref={dropdownRef}
          role="dialog"
          aria-label="Accessibility Options"
          className="rounded-2xl overflow-hidden w-80 sm:w-96 accessibility-panel-in"
          style={{
            backgroundColor: panelBg,
            border: `2px solid ${panelBorder}`,
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
            maxHeight: '75vh',
            overflowY: 'auto',
          }}
        >
          {/* Header */}
          <div className="px-5 py-4 flex items-center justify-between" style={{ backgroundColor: headerBg }}>
            <h2 className="font-semibold text-lg" style={{ color: headerText }}>Accessibility</h2>
            <button
              ref={closeButtonRef}
              onClick={() => { setIsOpen(false); buttonRef.current?.focus(); }}
              className="rounded-full p-1.5 hover:opacity-80 active:scale-90 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer"
              style={{ color: headerText }}
              aria-label="Close accessibility options"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Read Aloud */}
          <div className="px-5 py-4" style={{ borderBottom: `1px solid ${sectionBorder}` }}>
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" style={{ color: iconColor }}>
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
              </svg>
              <h3 className="font-semibold text-sm" style={{ color: labelColor }}>Read Page Aloud</h3>
            </div>
            <div className="flex gap-2">
              {!isReading ? (
                <button
                  onClick={speakText}
                  className="flex-1 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer select-none"
                  style={{ backgroundColor: accentBg, color: accentText }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = accentHoverBg; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = accentBg; }}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
                  Play
                </button>
              ) : (
                <>
                  <button
                    onClick={isPaused ? resumeSpeech : pauseSpeech}
                    className="flex-1 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer select-none hover:brightness-110"
                    style={{ backgroundColor: isPaused ? '#16a34a' : '#ca8a04', color: '#fff' }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      {isPaused ? <path d="M8 5v14l11-7z"/> : <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>}
                    </svg>
                    {isPaused ? 'Resume' : 'Pause'}
                  </button>
                  <button
                    onClick={stopSpeech}
                    className="flex-1 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer select-none hover:brightness-110"
                    style={{ backgroundColor: '#dc2626', color: '#fff' }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6h12v12H6z"/></svg>
                    Stop
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Text Size */}
          <div className="px-5 py-4" style={{ borderBottom: `1px solid ${sectionBorder}` }}>
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" style={{ color: iconColor }}>
                <path d="M9 4v3h5v12h3V7h5V4H9zm-6 8h3v7h3v-7h3V9H3v3z"/>
              </svg>
              <h3 className="font-semibold text-sm" style={{ color: labelColor }}>Text Size</h3>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {[['small', 'S'], ['medium', 'M'], ['large', 'L'], ['extra-large', 'XL']].map(([size, label]) => (
                <button
                  key={size}
                  onClick={() => setTextSize(size)}
                  className="py-2 rounded-lg text-sm font-medium transition-all duration-150 hover:scale-[1.03] active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer select-none"
                  style={{
                    backgroundColor: textSize === size ? selectedBg : 'transparent',
                    color: textSize === size ? selectedText : unselectedText,
                    border: `2px solid ${textSize === size ? selectedBorder : unselectedBorder}`,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Toggles */}
          {[
            { label: 'Reduce Motion', sub: 'Pause video and motion', icon: 'M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z', on: reduceMotion, toggle: toggleReduceMotion },
            { label: 'High Contrast', sub: 'Black, white & yellow palette', icon: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z', on: highContrast, toggle: toggleHighContrast },
          ].map(({ label, sub, icon, on, toggle }) => (
            <div key={label} className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${sectionBorder}` }}>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" style={{ color: iconColor }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                </svg>
                <div>
                  <h3 className="font-semibold text-sm" style={{ color: labelColor }}>{label}</h3>
                  <p className="text-xs" style={{ color: subColor }}>{sub}</p>
                </div>
              </div>
              <ToggleSwitch on={on} onToggle={toggle} label={label} />
            </div>
          ))}

          <div className="px-5 py-3 text-xs" style={{ backgroundColor: footerBg, color: footerText }}>
            Settings are saved for this device.
          </div>
        </div>
      )}

      {/* Trigger button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="rounded-full w-14 h-14 flex items-center justify-center transition-all duration-150 ease-smooth hover:scale-110 active:scale-95 focus:outline-none focus:ring-4 focus:ring-ifys-light-blue cursor-pointer select-none"
        style={{
          backgroundColor: hc ? '#ffff00' : '#0F3656',
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        }}
        aria-label="Accessibility options"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        title="Accessibility options"
      >
        <img
          src="/images/accessibility icon.png"
          alt=""
          aria-hidden="true"
          className="w-7 h-7"
          style={{ filter: hc ? 'invert(1)' : 'none' }}
        />
      </button>
    </div>
  );
};

export default AccessibilityWidget;
